import {useCallback, useEffect, useMemo, useState} from 'react';
import type {DashboardGroupByToken} from '../utils/groupTickets';

function storageKey(token: DashboardGroupByToken): string {
	return `dashboard.collapsedGroups.${token}.v1`;
}

function readPersisted(token: DashboardGroupByToken): Set<string> {
	if (typeof window === 'undefined') return new Set();
	try {
		const raw = window.localStorage.getItem(storageKey(token));
		if (!raw) return new Set();
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return new Set();
		return new Set(parsed.filter((k): k is string => typeof k === 'string'));
	} catch {
		return new Set();
	}
}

function writePersisted(token: DashboardGroupByToken, collapsed: ReadonlySet<string>) {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(storageKey(token), JSON.stringify([...collapsed]));
	} catch {
		// localStorage may be disabled or quota-exceeded; silently ignore.
	}
}

export interface UseDashboardGroupCollapse {
	isCollapsed: (groupKey: string) => boolean;
	toggle: (groupKey: string) => void;
}

/**
 * Per-group collapse state for the dashboard's grouped view.
 *
 * Each grouping token persists to its own key, so switching tokens neither leaks
 * collapse state across unrelated group sets nor forgets what was collapsed under
 * the token you came from.
 *
 * The collapsed set is held together with the token it belongs to, and a token
 * change is reconciled during render rather than in an effect. Two separate
 * effects — one loading on token change, one persisting on set change — would
 * both fire on the render where the token changed, and the persisting one would
 * write the OLD token's collapsed set under the NEW token's key before the load
 * landed. That self-heals on the following render, so it passes a test while
 * still writing wrong data to storage; keeping the pair in one state value means
 * the mismatch can't exist in the first place.
 *
 * Distinct from the queue's `useGroupCollapse`, which persists to a single
 * un-keyed bucket because the queue only ever groups by status.
 */
export function useDashboardGroupCollapse(token: DashboardGroupByToken): UseDashboardGroupCollapse {
	const [state, setState] = useState<{token: DashboardGroupByToken; collapsed: Set<string>}>(() => ({
		token,
		collapsed: readPersisted(token),
	}));

	// Reconcile during render: React discards this render's output and re-renders
	// with the new state before committing, so the token and the set it belongs to
	// are never observably out of step.
	const collapsed = state.token === token ? state.collapsed : readPersisted(token);
	if (state.token !== token) {
		setState({token, collapsed});
	}

	useEffect(() => {
		writePersisted(token, collapsed);
	}, [token, collapsed]);

	const toggle = useCallback((groupKey: string) => {
		setState(prev => {
			const next = new Set(prev.collapsed);
			if (next.has(groupKey)) next.delete(groupKey);
			else next.add(groupKey);
			return {token: prev.token, collapsed: next};
		});
	}, []);

	const isCollapsed = useCallback((groupKey: string) => collapsed.has(groupKey), [collapsed]);

	return useMemo(() => ({isCollapsed, toggle}), [isCollapsed, toggle]);
}
